import { useState, useEffect } from 'react';

/**
 * Hook to match a CSS media query
 *
 * Uses the window.matchMedia API for efficient, event-based detection.
 * Automatically updates when the viewport crosses query thresholds.
 * SSR-safe: returns false when window is not available.
 *
 * @param query - CSS media query string (e.g., '(min-width: 640px)')
 * @returns boolean - true if the media query matches
 *
 * @example Basic usage
 * ```tsx
 * function MyComponent() {
 *   const isLargeScreen = useMediaQuery('(min-width: 1024px)');
 *   return isLargeScreen ? <LargeLayout /> : <SmallLayout />;
 * }
 * ```
 *
 * @example With MEDIA_QUERIES constants
 * ```tsx
 * import { MEDIA_QUERIES } from '@/lib/constants/breakpoints';
 *
 * function MyComponent() {
 *   const isMobile = useMediaQuery(MEDIA_QUERIES.mobile);
 *   const isDesktop = useMediaQuery(MEDIA_QUERIES.desktop);
 *   // ...
 * }
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // SSR safety - default to false if window not available
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    // Handler for media query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Sync initial value (handles React 18 strict mode double-mount)
    setMatches(mediaQuery.matches);

    // Modern browsers use addEventListener
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}
